import { apiClient } from './api'
import type {
  PaymentTransaction,
  CreatePaymentRequest,
  RetryPaymentRequest,
} from '@/types/payment'

interface ApiResponse<T> {
  success: boolean
  data: T
  error?: {
    code: string
    message: string
  }
}

async function api<T>(promise: Promise<unknown>): Promise<ApiResponse<T>> {
  return promise as unknown as ApiResponse<T>
}

export const paymentService = {
  createPayment: async (request: CreatePaymentRequest): Promise<PaymentTransaction> => {
    const response = await api<Record<string, unknown>>(
      apiClient.post('/payments', {
        enrollment_id: request.enrollmentId,
        payment_channel: request.paymentChannel,
        customer_name: request.customerName,
        customer_email: request.customerEmail,
        customer_phone: request.customerPhone,
        coupon_code: request.couponCode,
        discount_amount: request.discountAmount,
      })
    )
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to create payment')
    }
    return transformPaymentTransaction(response.data)
  },

  getPaymentStatus: async (transactionId: string): Promise<PaymentTransaction> => {
    const response = await api<Record<string, unknown>>(
      apiClient.get(`/payments/${transactionId}/status`)
    )
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to get payment status')
    }
    return transformPaymentTransaction(response.data)
  },

  getPaymentsByEnrollment: async (enrollmentId: string): Promise<PaymentTransaction[]> => {
    const response = await api<Record<string, unknown>[]>(
      apiClient.get(`/payments/enrollment/${enrollmentId}`)
    )
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to get payments')
    }
    return (response.data || []).map(transformPaymentTransaction)
  },

  retryPayment: async (
    enrollmentId: string,
    request: RetryPaymentRequest
  ): Promise<PaymentTransaction> => {
    const response = await api<Record<string, unknown>>(
      apiClient.post(`/payments/enrollment/${enrollmentId}/retry`, {
        payment_channel: request.paymentChannel,
        customer_name: request.customerName,
        customer_email: request.customerEmail,
        customer_phone: request.customerPhone,
      })
    )
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to retry payment')
    }
    return transformPaymentTransaction(response.data)
  },
}

function transformPaymentTransaction(data: Record<string, unknown>): PaymentTransaction {
  return {
    id: data.id as string,
    enrollmentId: (data.enrollment_id || data.enrollmentId) as string,
    externalId: (data.external_id || data.externalId) as string,
    paymentChannel: (data.payment_channel || data.paymentChannel) as PaymentTransaction['paymentChannel'],
    paymentMethod: (data.payment_method || data.paymentMethod) as PaymentTransaction['paymentMethod'],
    amount: data.amount as number,
    fee: data.fee as number,
    discount: data.discount as number,
    netAmount: (data.net_amount || data.netAmount) as number,
    status: data.status as PaymentTransaction['status'],
    paymentCode: (data.payment_code || data.paymentCode) as string | undefined,
    paymentUrl: (data.payment_url || data.paymentUrl) as string | undefined,
    expiryTime: (data.expiry_time || data.expiryTime) as string | undefined,
    paidAt: (data.paid_at || data.paidAt) as string | undefined,
    couponCode: (data.coupon_code || data.couponCode) as string | undefined,
    createdAt: (data.created_at || data.createdAt) as string,
    updatedAt: (data.updated_at || data.updatedAt) as string,
  }
}

export default paymentService
